// src/hooks/useSoundEffects.js
// Centralized sound effects manager for the platform.
// Uses Web Audio API for true simultaneous/overlapping playback.

import { useCallback, useEffect, useRef } from 'react';

// Paths relative to the public folder
const SOUND_URLS = {
  join: '/join.wav',
  leave: '/leave.wav',
  directMessage: '/direct-message.wav',
};

// Pre-decode audio as AudioBuffers for instant overlapping playback
const audioBufferCache = {};
let audioContext = null;
let preloaded = false;

function getAudioContext() {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('[SoundEffects] Web Audio API not available');
    }
  }
  return audioContext;
}

async function preloadSounds() {
  if (preloaded) return;
  preloaded = true;

  for (const [key, url] of Object.entries(SOUND_URLS)) {
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const ctx = getAudioContext();
      if (ctx) {
        audioBufferCache[key] = await ctx.decodeAudioData(arrayBuffer);
      }
    } catch (e) {
      console.warn(`[SoundEffects] Failed to preload "${key}":`, e);
    }
  }
}

/**
 * Play a sound by key. Each call creates a new buffer source node,
 * so multiple plays of the same sound truly overlap.
 */
function playSound(key, volume = 0.5) {
  const ctx = getAudioContext();
  const buffer = audioBufferCache[key];

  if (ctx && buffer) {
    try {
      // Resume context if suspended (browser autoplay policy)
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      const gainNode = ctx.createGain();
      gainNode.gain.value = volume;

      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      source.start(0);
      return;
    } catch (e) {
      console.warn('[SoundEffects] Web Audio playback failed, trying HTML Audio:', e);
    }
  }

  // Fallback: plain HTML Audio (won't overlap but at least plays)
  const url = SOUND_URLS[key];
  if (!url) return;

  try {
    const audio = new Audio(url);
    audio.volume = volume;
    audio.play().catch(() => {});
  } catch {
    // Silently fail
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useSoundEffects() {
  useEffect(() => {
    preloadSounds();
  }, []);

  const playJoin = useCallback(() => {
    playSound('join', 0.5);
  }, []);

  const playLeave = useCallback(() => {
    playSound('leave', 0.5);
  }, []);

  const playDirectMessage = useCallback(() => {
    playSound('directMessage', 0.5);
  }, []);

  return { playJoin, playLeave, playDirectMessage };
}

export default useSoundEffects;